import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { IPendingTransactionForApproval } from '../../../types/pendingTransaction';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IGetPendingTransactionsRequest {
  /** Initiator's id */
  initiator?: string;
  /** 'transfer' | 'bill-payment' | 'betting' */
  type?: string;
  minAmount?: string | number;
  maxAmount?: string | number;
  from?: string;
  to?: string;
  /** Skip the error toast, for callers that show their own failure state */
  silent?: boolean;
}

interface IGetPendingTransactionsResponse {
  status?: boolean;
  message?: string;
  data?: IPendingTransactionForApproval[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetPendingTransactionsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getTransactionsPendingApprovalAsync = createAsyncThunk(
  'pendingTransactions/approval',
  async (
    {
      to = '',
      from = '',
      type = '',
      initiator = '',
      maxAmount,
      minAmount,
      silent = false,
    }: IGetPendingTransactionsRequest,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ??
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(
        `/transaction/outlet-mandates/pending?to=${to}&from=${from}&type=${type}&initiator=${initiator}&maxAmount=${
          maxAmount !== '' && maxAmount ? Number(maxAmount) * 100 : ''
        }&minAmount=${
          minAmount !== '' && minAmount ? Number(minAmount) * 100 : ''
        }`,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IGetPendingTransactionsResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
        customMessage:
          'An error occurred while trying to apply filters, try again',
      });

      if (!silent) {
        dispatch(
          showAlert({
            status: 'error',
            description: errorMessage,
          })
        );
      }

      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getPendingTransactionsSlice = createSlice({
  name: 'pendingTransactions/approval/get',
  initialState,
  reducers: {
    resetGetPendingTransactions: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransactionsPendingApprovalAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(
        getTransactionsPendingApprovalAsync.fulfilled,
        (state, action) => {
          state.status = 'success';
          state.value = action.payload as IGetPendingTransactionsResponse;
        }
      )
      .addCase(
        getTransactionsPendingApprovalAsync.rejected,
        (state, action) => {
          state.status = 'failed';
          state.value = action.payload as IGetPendingTransactionsResponse;
          state.error = action.error.message;
        }
      );
  },
});

export const { resetGetPendingTransactions } =
  getPendingTransactionsSlice.actions;

export default getPendingTransactionsSlice.reducer;
